import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { ArrowLeft, Plus, X, Pencil, Trash2 } from 'lucide-react';

export default function ChecklistManagePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showItem, setShowItem] = useState(false);
  const [editItemId, setEditItemId] = useState(null);
  const [itemForm, setItemForm] = useState({ title: '', description: '' });

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/checklists/${id}`).then((r) => setChecklist(r.data)).catch(() => setChecklist(null)).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const openAddItem = () => { setEditItemId(null); setItemForm({ title: '', description: '' }); setShowItem(true); };
  const openEditItem = (it) => { setEditItemId(it._id); setItemForm({ title: it.title, description: it.description || '' }); setShowItem(true); };

  const saveItem = async (e) => {
    e.preventDefault();
    try {
      if (editItemId) await api.put(`/checklists/${id}/items/${editItemId}`, itemForm);
      else await api.post(`/checklists/${id}/items`, itemForm);
      toast.success('Saved');
      // keep modal open for quick consecutive adds when creating
      if (editItemId) setShowItem(false);
      else setItemForm({ title: '', description: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    }
  };

  const deleteItem = async (it) => {
    if (!window.confirm(`Remove task "${it.title}"?`)) return;
    try {
      await api.delete(`/checklists/${id}/items/${it._id}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    }
  };

  if (loading) return <div className="page"><div className="empty-state">Loading...</div></div>;
  if (!checklist) return <div className="page"><div className="empty-state">Checklist not found.</div></div>;

  const items = checklist.items || [];

  return (
    <div className="page">
      <div className="page-header">
        <div className="staff-header-card" style={{ border: 'none', padding: 0, margin: 0, background: 'transparent' }}>
          <button className="back-arrow-btn" onClick={() => navigate('/checklist')} title="Back">
            <ArrowLeft size={22} />
          </button>
          <div>
            <h2 style={{ fontSize: 20 }}>{checklist.name}</h2>
            <p className="text-muted">{items.length} task{items.length === 1 ? '' : 's'} in this checklist</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={openAddItem}>
            <Plus size={16} /> Add Task
          </button>
        </div>
      </div>

      <div className="checklist-list">
        {items.length === 0 && (
          <div className="empty-state">No tasks yet. Use “Add Task” to build this checklist.</div>
        )}
        {items.map((it, idx) => (
          <div key={it._id} className="checklist-row">
            <div className="checklist-text">
              <span className="checklist-title">{idx + 1}. {it.title}</span>
              {it.description && <span className="checklist-desc">{it.description}</span>}
            </div>
            <div className="checklist-meta">
              <button className="btn btn-sm btn-outline btn-icon" onClick={() => openEditItem(it)} title="Edit"><Pencil size={13} /></button>
              <button className="btn btn-sm btn-outline btn-icon" onClick={() => deleteItem(it)} title="Remove"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>

      {showItem && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-row">
              <h3>{editItemId ? 'Edit Task' : 'Add Task'}</h3>
              <button className="btn btn-sm btn-outline btn-icon" onClick={() => setShowItem(false)}><X size={16} /></button>
            </div>
            <form onSubmit={saveItem}>
              <div className="form-group">
                <label>Task Title *</label>
                <input type="text" value={itemForm.title} onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })} placeholder="e.g. Clean counter table" autoFocus required />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input type="text" value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} placeholder="Optional details" />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowItem(false)}>
                  {editItemId ? 'Cancel' : 'Done'}
                </button>
                <button type="submit" className="btn btn-primary">{editItemId ? 'Save' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
